/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import {
  GET_CAMPAIGNS,
  GET_RECENT_AUTOMATIONS_RUN,
  GET_RECENT_EMAILS_EDITED,
  GET_RECENT_EMAILS_SENT,
  GET_RECENT_JOURNEYS_EDITED,
  GET_SALESFORCE_BLOG_POSTS,
  LEGACY_MODE_FUEL,
} from '../constants';

import { createFetchAction, createMcGetAction, createMcPostAction, createMcLegacyGetAction } from '../api';

/* eslint-disable max-len */

/* This will simply check for localStorage so we don't throw any errrors after privacy changes or if cookies are blocked */
function checkLocalStorage() {
  try {
    window.localStorage.setItem('hasLocalStorage');
    window.localStorage.removeItem('hasLocalStorage');

    return true;
  } catch (_e) {
    return false;
  }
}

function useOpenrastaProxy() {
  let useProxy = false; // Set a default here in case local storage is not available
  const envVar = window.APPAUTHPROXY_LEGACY_MODE;
  const legacyMode = (envVar || '').toUpperCase();
  const hasLocalStorage = checkLocalStorage();

  if (legacyMode === LEGACY_MODE_FUEL) {
    useProxy = true;
  }

  if (hasLocalStorage && 'HomepageOpenRastaToFuel' in window.localStorage) {
    useProxy = Boolean(window.localStorage.getItem('HomepageOpenRastaToFuel') === 'true');
  }

  return useProxy;
}

export function getCampaigns(options) {
  return createMcGetAction(GET_CAMPAIGNS, 'hub-internal/v1/campaigns/?$pageSize=50&$page=1', options);
}

export function getRecentAutomationsRun(options) {
  const url = 'beta/automations/automation/definition/?$top=5&$skip=0&$sort=lastRunTime%20desc&view=gridView';

  if (useOpenrastaProxy()) {
    return createMcGetAction(GET_RECENT_AUTOMATIONS_RUN, `legacy/v1/${url}`, options);
  }

  return createMcLegacyGetAction(GET_RECENT_AUTOMATIONS_RUN, `rest/${url}`, options);
}

export function getRecentEmailsEdited(options = {}) {
  // You can find out more about building a query for this route at https://confluence.internal.salesforce.com/display/MC/Advanced+Asset+Query
  return createMcPostAction(GET_RECENT_EMAILS_EDITED, 'asset/v1/content/assets/query', {
    data: {
      page: {
        page: 1,
        pageSize: 5,
      },
      sort: [
        {
          property: 'modifiedDate',
          direction: 'desc',
        },
      ],
      query: {
        leftOperand: {
          property: 'assetType.id',
          simpleOperator: 'in',
          values: [5, 207, 208, 209],
        },
        logicalOperator: 'AND',
        rightOperand: {
          property: 'data.email.legacy.legacyId',
          simpleOperator: 'greaterThan',
          value: 0,
        },
      },
      fields: ['campaigns', 'id', 'modifiedBy', 'modifiedDate', 'name', 'objectID', 'data'],
    },
    ...options,
  });
}

export function getRecentEmailsSent(options) {
  return createMcGetAction(
    GET_RECENT_EMAILS_SENT,
    'internal/v1/sends?status=Complete&$orderBy=sendTime%20DESC',
    options
  );
}

export function getRecentJourneysEdited(options) {
  return createMcGetAction(
    GET_RECENT_JOURNEYS_EDITED,
    'interaction/v1/interactions/?mostRecentVersionOnly=false&mostRecentVersionOrRunningOnly=true&$page=1&$pageSize=5&$orderBy=modifiedDate%20DESC&extras=stats',
    options
  );
}

export function getSalesforceBlogPosts(options) {
  return createFetchAction(GET_SALESFORCE_BLOG_POSTS, '/blog/salesforce', options);
}

/* eslint-enable max-len */
